import React from "react";
// Chakra imports
import {
    Box,
    Flex,
    Button,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Text,
    FormErrorMessage,
    useToast,
    VStack,
    Icon,
    InputGroup,
    InputLeftElement,
    Image,
    useColorModeValue,
} from "@chakra-ui/react";
import { useLoading } from "Providers/LoadingProvider";
import { Link, useHistory, useParams } from "react-router-dom";
import { Field, Form, Formik } from "formik";
import AuthApi from "Api/AuthApi";
import { TbLock, TbKey } from "react-icons/tb";

function RecoverToken() {
    const loading = useLoading();
    const history = useHistory();
    const toast = useToast({
        position: "top-right",
    });
    let { token } = useParams();

    let submitHandle = async (values, { setFieldError }) => {
        loading.show();
        let response = await AuthApi.redefine(token, values);
        if (response.status !== 200) {
            if (response.status === 422) {
                let errors = response.json.errors;
                for (let field in errors) {
                    setFieldError(field, errors[field].join(" "));
                }
            } else {
                toast({ status: 'error', title: response.json.error || 'Erro ao recuperar senha', duration: 3000, variant: 'solid', isClosable: true });
            }
        } else {
            toast({ status: 'success', title: 'Senha alterada com sucesso!', duration: 3000, variant: 'solid', isClosable: true });
            history.push("/auth/signin");
        }
        loading.hide();
    }

    // Dark mode colors
    const cardBg = useColorModeValue("white", "gray.800");
    const inputBg = useColorModeValue("gray.50", "gray.700");
    const inputBorder = useColorModeValue("gray.200", "gray.600");
    const inputHoverBorder = useColorModeValue("gray.300", "gray.500");
    const inputFocusBg = useColorModeValue("white", "gray.700");
    const labelColor = useColorModeValue("gray.700", "gray.200");
    const textMuted = useColorModeValue("gray.500", "gray.400");
    const iconColor = useColorModeValue("gray.400", "gray.500");
    const linkColor = useColorModeValue("#2D6B3F", "green.300");

    return (
        <Flex w="100%" justify="center" px={4}>
            <Box
                w={{ base: "100%", md: "420px" }}
                bg={cardBg}
                borderRadius="2xl"
                boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                overflow="hidden"
            >
                {/* Header */}
                <Box
                    bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                    py={8}
                    px={6}
                    textAlign="center"
                >
                    <Link to="/">
                        <Image
                            src="/wsclima-logo.png"
                            alt="WS Clima Logo"
                            h="60px"
                            w="auto"
                            mx="auto"
                            mb={3}
                            objectFit="contain"
                        />
                    </Link>
                    <Heading color="white" fontSize="2xl" fontWeight="bold">
                        Redefinição de Senha
                    </Heading>
                    <Text color="whiteAlpha.800" fontSize="sm" mt={2}>
                        Crie uma nova senha para acessar o sistema
                    </Text>
                </Box>

                {/* Form */}
                <Box p={8}>
                    <Formik onSubmit={submitHandle} initialValues={{ password: "", password_confirmation: "" }}>
                        <Form>
                            <VStack spacing={5}>
                                <Field name="password">
                                    {({ field, form }) => (
                                        <FormControl isInvalid={form.errors.password && form.touched.password}>
                                            <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                Nova Senha
                                            </FormLabel>
                                            <InputGroup size="lg">
                                                <InputLeftElement pointerEvents="none" h="100%">
                                                    <Icon as={TbLock} color={iconColor} />
                                                </InputLeftElement>
                                                <Input
                                                    {...field}
                                                    borderRadius="xl"
                                                    fontSize="sm"
                                                    type="password"
                                                    placeholder="••••••••"
                                                    bg={inputBg}
                                                    border="2px solid"
                                                    borderColor={inputBorder}
                                                    _hover={{ borderColor: inputHoverBorder }}
                                                    _focus={{ borderColor: "#2D6B3F", bg: inputFocusBg, boxShadow: "0 0 0 1px #2D6B3F" }}
                                                />
                                            </InputGroup>
                                            <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>

                                <Field name="password_confirmation">
                                    {({ field, form }) => (
                                        <FormControl isInvalid={form.errors.password_confirmation && form.touched.password_confirmation}>
                                            <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                Confirme a Senha
                                            </FormLabel>
                                            <InputGroup size="lg">
                                                <InputLeftElement pointerEvents="none" h="100%">
                                                    <Icon as={TbLock} color={iconColor} />
                                                </InputLeftElement>
                                                <Input
                                                    {...field}
                                                    borderRadius="xl"
                                                    fontSize="sm"
                                                    type="password"
                                                    placeholder="••••••••"
                                                    bg={inputBg}
                                                    border="2px solid"
                                                    borderColor={inputBorder}
                                                    _hover={{ borderColor: inputHoverBorder }}
                                                    _focus={{ borderColor: "#2D6B3F", bg: inputFocusBg, boxShadow: "0 0 0 1px #2D6B3F" }}
                                                />
                                            </InputGroup>
                                            <FormErrorMessage>{form.errors.password_confirmation}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>

                                <Button
                                    type="submit"
                                    bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                                    color="white"
                                    w="100%"
                                    h="50px"
                                    borderRadius="xl"
                                    fontSize="md"
                                    fontWeight="semibold"
                                    _hover={{
                                        bg: "linear-gradient(135deg, #1F5A2F 0%, #164A24 100%)",
                                        transform: "translateY(-2px)",
                                        boxShadow: "lg",
                                    }}
                                    _active={{
                                        transform: "translateY(0)",
                                    }}
                                    transition="all 0.2s"
                                    leftIcon={<TbKey />}
                                >
                                    Redefinir minha senha
                                </Button>
                            </VStack>
                        </Form>
                    </Formik>

                    {/* Link */}
                    <VStack spacing={3} mt={6}>
                        <Text color={textMuted} fontSize="sm" textAlign="center">
                            Lembrou sua senha?{" "}
                            <Link to="/auth/signin">
                                <Text as="span" color={linkColor} fontWeight="bold" _hover={{ textDecoration: "underline" }}>
                                    Faça login
                                </Text>
                            </Link>
                        </Text>
                    </VStack>
                </Box>
            </Box>
        </Flex>
    );
}

export default RecoverToken;
